import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/电影' },
  { label: '电视剧', to: '/category/电视剧' },
  { label: '资讯', to: '/news' },
  { label: '搜索', to: '/search' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? 'border-background-200 bg-background-50/95 backdrop-blur-md'
          : 'border-transparent bg-background-50/70 backdrop-blur-sm'
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-portal items-center gap-6 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2" onClick={() => setMenuOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-clapperboard-fill text-lg" />
          </span>
          <span className="font-heading text-xl font-black tracking-tight text-foreground-950">影视仓</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-primary-600'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="ml-auto hidden max-w-xs flex-1 items-center md:flex lg:max-w-sm">
          <div className="flex w-full items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2 transition-colors focus-within:border-primary-300">
            <i className="ri-search-line text-sm text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、电视剧或资讯"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="hidden whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 lg:block"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="菜单"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
        >
          <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 md:px-6 lg:hidden">
          <form onSubmit={handleSearch} className="mb-3 md:hidden">
            <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2.5">
              <i className="ri-search-line text-sm text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、电视剧或资讯"
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100 hover:text-primary-600'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}